import CSS from 'csstype';

import { fontColorsDefault } from '~/src/dash/components/layout/common';

const FONT_SIZE_REM = {
  mini: 0.625,
  extraSmall: 0.75,
  small: 0.875,
  base: 1,
  large: 1.125,
  extraLarge: 1.5,
};

const FONT_SIZE_PIXELS = {
  mini: 10,
  extraSmall: 12,
  small: 14,
  base: 16,
  large: 18,
  extraLarge: 24,
};

export type fontSize = 'mini' | 'extraSmall' | 'small' | 'base' | 'large' | 'extraLarge';

export const fontSizing = {
  mini: `${FONT_SIZE_PIXELS.mini}px`,
  extraSmall: `${FONT_SIZE_PIXELS.extraSmall}px`,
  small: `${FONT_SIZE_PIXELS.small}px`,
  base: `${FONT_SIZE_PIXELS.base}px`,
  large: `${FONT_SIZE_PIXELS.large}px`,
  extraLarge: `${FONT_SIZE_PIXELS.extraLarge}px`,
} as Record<fontSize, string>;

export const fontSizingV2 = {
  mini: `${FONT_SIZE_REM.mini}rem`,
  extraSmall: `${FONT_SIZE_REM.extraSmall}rem`,
  small: `${FONT_SIZE_REM.small}rem`,
  base: `${FONT_SIZE_REM.base}rem`,
  large: `${FONT_SIZE_REM.large}rem`,
  extraLarge: `${FONT_SIZE_REM.extraLarge}rem`,
} as Record<fontSize, string>;

export type fontWeight = 'none' | 'base' | 'bold' | 'heavy';

export const FONT_WEIGHT = {
  none: 400,
  base: 400,
  bold: 500,
  heavy: 600,
} as Record<fontWeight, number>;

interface IStyleProp {
  [key: string]: boolean | undefined;
}

export const getFontSize = ({
  mini,
  extraSmall,
  small,
  baseSize,
  large,
  extraLarge,
}: IStyleProp) => {
  switch (true) {
    case mini:
      return FONT_SIZE_PIXELS.mini;
    case extraSmall:
      return FONT_SIZE_PIXELS.extraSmall;
    case small:
      return FONT_SIZE_PIXELS.small;
    case large:
      return FONT_SIZE_PIXELS.large;
    case extraLarge:
      return FONT_SIZE_PIXELS.extraLarge;
    case baseSize:
    default:
      return FONT_SIZE_PIXELS.base;
  }
};

export const getFontSizeV2 = ({
  mini,
  extraSmall,
  small,
  baseSize,
  large,
  extraLarge,
}: IStyleProp): number => {
  switch (true) {
    case mini:
      return FONT_SIZE_REM.mini;
    case extraSmall:
      return FONT_SIZE_REM.extraSmall;
    case small:
      return FONT_SIZE_REM.small;
    case large:
      return FONT_SIZE_REM.large;
    case extraLarge:
      return FONT_SIZE_REM.extraLarge;
    case baseSize:
    default:
      return FONT_SIZE_REM.base;
  }
};

export const getLineHeightV2 = (fontSize: fontSize) => {
  return FONT_SIZE_REM[fontSize] * 0.101;
};

export const getLineHeight = (fontSize: fontSize) => {
  return FONT_SIZE_PIXELS[fontSize] * 1.618;
};

export type fontColorType = 'muted' | 'base' | 'highlighted';

interface IGetFontColorProps {
  fontColors: Record<fontColorType, string>;
  muted: boolean | undefined;
  baseColor: boolean | undefined;
  highlighted: boolean | undefined;
}

export const getFontColor = ({
  fontColors = fontColorsDefault,
  muted,
  baseColor,
  highlighted,
}: IGetFontColorProps) => {
  switch (true) {
    case muted:
      return fontColors.muted;
    case highlighted:
      return fontColors.highlighted;
    case baseColor:
    default:
      return fontColors.base;
  }
};

export const getFontWeight = ({ baseWeight, bold, heavy }: IStyleProp) => {
  switch (true) {
    case bold:
      return FONT_WEIGHT.bold;
    case heavy:
      return FONT_WEIGHT.heavy;
    case baseWeight:
    default:
      return FONT_WEIGHT.base;
  }
};

export const getFontFamily = ({ roboto }: IStyleProp) => {
  switch (true) {
    case roboto:
      return 'Roboto';
    default:
      return 'inherit';
  }
};

export const getFontPresetStyle = ({ header, subheader, title }: IStyleProp) => {
  switch (true) {
    case header:
      return {
        fontSize: fontSizing.extraLarge,
        lineHeight: `${getLineHeight('extraLarge')}px`,
        fontWeight: FONT_WEIGHT.heavy,
      };
    case subheader:
      return {
        fontSize: fontSizing.large,
        lineHeight: `${getLineHeight('large')}px`,
        fontWeight: FONT_WEIGHT.bold,
      };
    case title:
      return {
        fontSize: fontSizing.base,
        lineHeight: `${getLineHeight('base')}px`,
        fontWeight: FONT_WEIGHT.bold,
      };
    default:
      return {};
  }
};

export const disableSelectTextStyle: CSS.Properties = {
  MozUserSelect: 'none',
  WebkitUserSelect: 'none',
  msUserSelect: 'none',
  userSelect: 'none',
};
